import React, { useEffect, useMemo, useRef } from 'react';
import { Loader } from 'src/@vymo/ui/atoms';
import ProgressBar from 'src/@vymo/ui/atoms/progressBar';
import { Type } from 'src/@vymo/ui/atoms/progressBar/types';
import Text from 'src/@vymo/ui/atoms/text';
import { Banner, Card } from 'src/@vymo/ui/blocks';
import Form from 'src/@vymo/ui/molecules/form';
import { FormVersion } from 'src/@vymo/ui/molecules/form/types';
import { ReactComponent as Cancel } from 'src/assets/icons/cross.svg';
import { ReactComponent as Download } from 'src/assets/icons/download-line.svg';
import { Keys, locale } from 'src/i18n';
import {
  clearModuleButtons,
  setModuleButtons,
} from 'src/models/moduleButtons/slice';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { setRouteModuleBreadCrumbParams } from 'src/workspace/slice';
import { getNotification } from '../selectors';
import { setError } from '../slice';
import {
  CANCELLED,
  COMPLETED,
  IN_PROGRESS,
  jobDetailsInputs,
  statusTextKeyMap,
  statusVariantMap,
} from './constants';
import { getDefaultValues } from './queries';
import { getBulkDownloadJobData, getJobStatus } from './selectors';
import { cancelDownloadJob } from './thunk';
import styles from './index.module.scss';

function DdpDetails({ jobId }) {
  const dispatch = useAppDispatch();
  const jobData = useAppSelector((state) =>
    getBulkDownloadJobData(state, jobId),
  );
  const { message, variant } = useAppSelector(getNotification);

  const formattedJobData = useMemo(() => {
    if (!jobData) return jobData;

    return {
      ...jobData,
      status: jobData.status?.split('_').join(' '),
    };
  }, [jobData]);

  const isJobLoading = useAppSelector(getJobStatus);
  const formRef = useRef(null);

  const progressValue = useMemo(
    () =>
      formattedJobData?.totalSteps
        ? Math.round(
            (Number(formattedJobData?.currentStep) /
              Number(formattedJobData?.totalSteps)) *
              100,
          )
        : 0,
    [formattedJobData?.currentStep, formattedJobData?.totalSteps],
  );

  const { downloadLink } = useAppSelector((state) =>
    getBulkDownloadJobData(state, jobId),
  );

  const onDownloadReport = () => {
    try {
      const link = document.createElement('a');
      link.href = downloadLink;
      link.download = '';
      link.rel = 'noopener noreferrer';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (errorMessage) {
      dispatch(
        setError({
          message: locale(Keys.ERROR_DOWNLOADING_FILE),
        }),
      );
    }
  };

  const onCancelDownload = () => {
    dispatch(cancelDownloadJob(jobId));
  };

  useEffect(() => {
    const moduleButtons: any[] = [];

    if (jobData?.status === COMPLETED) {
      moduleButtons.push({
        label: locale(Keys.DOWNLOAD_REPORT),
        onClick: onDownloadReport,
        variant: 'default',
        type: 'primary',
        icon: <Download />,
        iconPosition: 'left',
        'data-test-id': 'download-report-button',
      });
    }

    if (jobData?.status === IN_PROGRESS) {
      moduleButtons.push({
        label: locale(Keys.CANCEL_DOWNLOAD),
        onClick: onCancelDownload,
        variant: 'subtle',
        type: 'outlined',
        icon: <Cancel />,
        iconPosition: 'left',
        'data-test-id': 'cancel-download-button',
      });
    }

    dispatch(setModuleButtons(moduleButtons));

    return () => {
      dispatch(clearModuleButtons());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobData?.status, jobId, downloadLink, dispatch]);

  const defaultValue = getDefaultValues(formattedJobData);

  const modifiedJobDetailsInputs = useMemo(
    () =>
      jobDetailsInputs.map((item) => {
        if (item.code !== 'status') {
          return item;
        }

        return {
          ...item,
          itemDisplayConfig: {
            ...(item.itemDisplayConfig ?? {}),
            type: 'tag',
            variant: statusVariantMap[jobData?.status] ?? 'default',
          },
        };
      }),
    [jobData?.status],
  );

  useEffect(() => {
    dispatch(
      setRouteModuleBreadCrumbParams({
        jobId,
      }),
    );
  }, [dispatch, jobId]);

  return (
    <div className={styles.ddpDetails}>
      {message && (
        <Banner
          message={message}
          variant={variant}
          position="topRight"
          duration={5000}
          closeable
        />
      )}
      <div className={styles.ddpDetails__body}>
        <div className={styles.ddpDetails__cardContainer}>
          <Card classNames={styles.ddpDetails__cardContainer__card}>
            <Text
              classNames={styles.ddpDetails__cardContainer__card__cardTitle}
              type="h5"
            >
              {locale(Keys.PROGRESS)}
            </Text>

            <div
              className={
                styles.ddpDetails__cardContainer__card__progressBarContainer
              }
            >
              <ProgressBar
                value={jobData.status === CANCELLED ? 100 : progressValue}
                type={Type.Circle}
                strokeColor="var(--icon-status-success-subtle)"
                innerRadius={90}
                variant={jobData.status === CANCELLED ? 'error' : undefined}
                size="large"
                // eslint-disable-next-line react/no-unstable-nested-components
                info={() =>
                  jobData?.status !== CANCELLED && (
                    <div
                      className={
                        styles.ddpDetails__cardContainer__card__progressBarContainer__info
                      }
                    >
                      <Text
                        classNames={
                          styles.ddpDetails__cardContainer__card__progressBarContainer__info__progressValue
                        }
                      >
                        {`${progressValue}%`}
                      </Text>

                      {formattedJobData?.status === COMPLETED && (
                        <Text
                          classNames={
                            styles.ddpDetails__cardContainer__card__progressBarContainer__info__subText
                          }
                        >
                          {locale(Keys.COMPLETED)}
                        </Text>
                      )}
                    </div>
                  )
                }
              />

              <Text
                type="h6"
                classNames={
                  styles.ddpDetails__cardContainer__card__progressBarContainer__description
                }
              >
                {locale(statusTextKeyMap[formattedJobData?.status])}
              </Text>
            </div>
          </Card>

          <Card classNames={styles.ddpDetails__cardContainer__card}>
            <div
              className={
                styles.ddpDetails__cardContainer__card__jobDetailsContainer
              }
            >
              <Text
                type="h5"
                classNames={styles.ddpDetails__cardContainer__card__cardTitle}
              >
                {locale(Keys.JOB_DETAILS)}
              </Text>
              {!isJobLoading ? (
                <Form
                  className={
                    styles.ddpDetails__cardContainer__card__jobDetailsContainer__form
                  }
                  ref={formRef}
                  id="jobDetailsCardForm"
                  name="jobDetailsCardForm"
                  span="1fr"
                  onChange={() => {}}
                  config={{
                    version: FormVersion.webRevyre,
                    data: modifiedJobDetailsInputs,
                    grouping: [],
                    fieldItemConfig: {
                      showDisabledIcon: true,
                    },
                    viewMode: true,
                  }}
                  value={defaultValue}
                />
              ) : (
                <Loader
                  className={
                    styles.ddpDetails__cardContainer__card__jobDetailsContainer__loader
                  }
                />
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default DdpDetails;
